// Per-building space + tenancy attributes — the "click a building, see what's
// inside" layer the Office of Physical Systems asked for (rooms used/unused +
// who rents the commercial space).
//
// DATA CONTRACT — this is the shape the dashboard joins onto a building by its
// GeoJSON `id`. Today it's a small SAMPLE seed (source: "pmcu-sample") so the
// mechanism is demoable and honest. When PMCU sends the real spreadsheets /
// when room-booking + BEMS feeds come online, replace this map with a live
// `/api/building-attributes` feed (same keys) — the UI does not change.
//
// Honesty rule (CLAUDE.md §11.4 / §12): sample rows are clearly labelled in the
// card. Tenant labels here are illustrative ("Retail unit", "Café concession"),
// never invented company names presented as real.

export type TenantCategory =
  | "retail" | "f&b" | "service" | "office" | "academic" | "telecom" | "other";

export interface BuildingTenant {
  name: string;
  category: TenantCategory;
  space?: string;            // unit / floor label
  leaseEnd?: string;         // ISO date — drives the "expiring" flag
  status?: "active" | "expiring" | "vacant";
}

export interface BuildingAttributes {
  /** Occupancy snapshot — total rooms/units vs. how many are in use right now. */
  rooms?: { total: number; occupied: number };
  /** Commercial / concession tenancy. */
  tenants?: BuildingTenant[];
  /** When the underlying data was captured. */
  asOf?: string;
  /** Provenance — "pmcu-sample" renders a clear SAMPLE badge on the card. */
  source: "pmcu-sample" | "pmcu" | "bems" | "booking";
}

// Keyed by GeoJSON feature.properties.id (e.g. "way/33070949").
export const BUILDING_ATTRIBUTES: Record<string, BuildingAttributes> = {
  // Commercial — PMCU office tower. Tenancy is the headline here.
  "way/33070949": {
    rooms: { total: 42, occupied: 37 },
    tenants: [
      { name: "Co-working operator", category: "office", space: "Floors 3–6", status: "active", leaseEnd: "2027-09-30" },
      { name: "Retail unit", category: "retail", space: "Ground floor, unit G-04", status: "active", leaseEnd: "2026-08-31" },
      { name: "Café concession", category: "f&b", space: "Ground floor atrium", status: "expiring", leaseEnd: "2026-07-15" },
      { name: "Telecom concession", category: "telecom", space: "Rooftop + riser", status: "active", leaseEnd: "2028-01-31" },
    ],
    asOf: "2026-06-02",
    source: "pmcu-sample",
  },
  // Residential / mixed — PMCU apartments.
  "way/33070964": {
    rooms: { total: 120, occupied: 104 },
    tenants: [
      { name: "Convenience retail", category: "retail", space: "Ground floor", status: "active", leaseEnd: "2026-12-31" },
      { name: "Laundry service", category: "service", space: "Basement", status: "active" },
    ],
    asOf: "2026-06-02",
    source: "pmcu-sample",
  },
  // Academic — classroom utilisation (the under-scheduling question).
  "way/31990343": {
    rooms: { total: 28, occupied: 11 },
    asOf: "2026-06-02",
    source: "pmcu-sample",
  },
  "way/107505949": {
    rooms: { total: 34, occupied: 19 },
    asOf: "2026-06-02",
    source: "pmcu-sample",
  },
  // Hospital block — operational occupancy.
  "way/31973904": {
    rooms: { total: 64, occupied: 58 },
    asOf: "2026-06-02",
    source: "pmcu-sample",
  },
  // Sports venue — event-driven, mostly idle between bookings.
  "way/33071318": {
    rooms: { total: 6, occupied: 1 },
    tenants: [
      { name: "Sports retail concession", category: "retail", space: "Concourse", status: "active" },
    ],
    asOf: "2026-06-02",
    source: "pmcu-sample",
  },
};

export function getBuildingAttributes(id?: string | null): BuildingAttributes | null {
  if (!id) return null;
  return BUILDING_ATTRIBUTES[id] ?? null;
}
